import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Spin } from "antd";
import { ArrowLeft } from "lucide-react";

import { AN_COLORS, AN_FONT, AN_MONO } from "../../../../design-system/tokens";
import { findJumpServer } from "../../../../service/sshProxyService";
import { HEARTBEAT_INTERVAL_SECONDS, proxyLiveness } from "../sshProxyModel";
import { StatusDot } from "../sshProxyUi";

import OverviewTab from "./tabs/OverviewTab";

// Per-gateway detail page.
//
// This is where everything that OUTLIVES onboarding lives. The wizard is
// one-time setup; these are settings. Keeping them here rather than as wizard
// steps means an admin can change a policy or turn recording off without
// re-running an onboarding flow against a gateway that is already running.
//
// The row is polled on the heartbeat cadence, because most of what these tabs
// display — liveness, version, the host key fingerprint — is reported by the gateway and
// does not exist until it starts.
export default function GatewayDetail() {
  const { jumpServerId } = useParams();
  const navigate = useNavigate();

  const [gateway, setGateway] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(() => {
    findJumpServer({
      jumpServerId,
      onSuccess: (row) => {
        setLoading(false);
        if (row) setGateway(row);
        else setNotFound(true);
      },
      onFailure: () => {
        setLoading(false);
        setNotFound(true);
      },
    });
  }, [jumpServerId]);

  useEffect(() => {
    load();
    const id = setInterval(load, HEARTBEAT_INTERVAL_SECONDS * 1000);
    return () => clearInterval(id);
  }, [load]);

  const live = proxyLiveness(gateway?.lastSeenAt);


  return (
    <div style={{ minHeight: "calc(100vh - 64px)", background: AN_COLORS.bgWarm, color: AN_COLORS.ink, fontFamily: AN_FONT, padding: "22px 28px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 16 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: AN_MONO, fontSize: 10.5, letterSpacing: 1.4, textTransform: "uppercase", color: AN_COLORS.muted, marginBottom: 6 }}>
            PAM · Gateway
          </div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: -0.4, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {gateway?.serverName || (loading ? "…" : `Gateway #${jumpServerId}`)}
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 500, color: AN_COLORS.ink2, border: `1px solid ${AN_COLORS.line}`, background: AN_COLORS.surface, borderRadius: 999, padding: "3px 11px" }}>
              <StatusDot tone={live.tone} />
              {live.label}
            </span>
          </h1>
          <div style={{ fontSize: 12.5, color: AN_COLORS.muted, marginTop: 5, fontFamily: AN_MONO }}>
            {gateway?.publicIpAddress || "—"}
            {gateway?.proxyListen ? ` ${gateway.proxyListen}` : ""}
            {gateway?.region ? ` · ${gateway.region}` : ""}
          </div>
        </div>
        <Button onClick={() => navigate("/pam/jumpServer")} icon={<ArrowLeft size={14} style={{ verticalAlign: -2, marginRight: 6 }} />}>
          All gateways
        </Button>
      </div>

      {loading && !gateway ? (
        <div style={{ padding: 60, textAlign: "center" }}>
          <Spin />
        </div>
      ) : notFound && !gateway ? (
        <div style={{ background: AN_COLORS.surface, border: `1px solid ${AN_COLORS.line}`, borderRadius: 10, padding: 40, textAlign: "center", color: AN_COLORS.muted, fontSize: 13 }}>
          No gateway with id <span style={{ fontFamily: AN_MONO }}>{jumpServerId}</span>. It may have been deleted.
        </div>
      ) : (
        // Overview is the only section, so there is no tab bar. Old links that
        // carry ?tab=logs / ?tab=policy / ?tab=identity simply land here.
        <OverviewTab gateway={gateway} jumpServerId={jumpServerId} reload={load} />
      )}
    </div>
  );
}
